import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { TURNSTILE_SITE_KEY } from '../../config/env';
import { logger } from '../../utils/logger';
import TurnstileWidget from '../../components/website/TurnstileWidget';

const workflowOptions = [
    {
        id: 'contact',
        label: 'Contact information',
        title: 'Leave your contact information',
        description: 'Leave your details so the AmpTrans team can follow up after ACT Expo 2026.',
    },
    {
        id: 'demo',
        label: 'Demo sign up',
        title: 'Book a product demo',
        description: 'Tell us what you want to see and we will coordinate a demo time.',
    },
    {
        id: 'register',
        label: 'User registration',
        title: 'Create an AmpTrans account',
        description: 'Sign up now for access to our tools.',
    },
];

const inputClassName =
    'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100';

const labelClassName = 'mb-2 block text-sm font-medium text-slate-700';

const initialContactForm = {
    name: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    interest: '',
};

const initialDemoForm = {
    name: '',
    email: '',
    company: '',
    role: '',
    productInterest: 'Utility planning',
    preferredTime: '',
    notes: '',
};

const initialRegistrationForm = {
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    company: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
};

const buildContactMessage = (heading, fields) => {
    const lines = Object.entries(fields)
        .filter(([, value]) => String(value || '').trim())
        .map(([key, value]) => `${key}: ${value}`);
    return `${heading}\n\n${lines.join('\n')}`;
};

function ACT2026Registration() {
    const [activeWorkflow, setActiveWorkflow] = useState('contact');
    const [contactForm, setContactForm] = useState(initialContactForm);
    const [demoForm, setDemoForm] = useState(initialDemoForm);
    const [registrationForm, setRegistrationForm] = useState(initialRegistrationForm);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsContent, setTermsContent] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const turnstileRef = useRef(null);
    const [website, setWebsite] = useState('');

    useEffect(() => {
        if (!showTermsModal) return;

        fetch('/terms-and-conditions.html')
            .then((response) => response.text())
            .then((html) => {
                const sanitizedHtml = DOMPurify.sanitize(`<div>${html}</div>`);
                setTermsContent(sanitizedHtml);
            })
            .catch((error) => {
                logger.error('Error fetching terms', error);
                setTermsContent('<p>Unable to load terms and conditions. Please try again later.</p>');
            });
    }, [showTermsModal]);

    const updateContactField = (field, value) => {
        setContactForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateDemoField = (field, value) => {
        setDemoForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateRegistrationField = (field, value) => {
        setRegistrationForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetMessage = () => setMessage({ type: '', text: '' });

    const getTurnstileToken = async () => {
        if (!TURNSTILE_SITE_KEY) return '';
        if (turnstileRef.current?.getFreshToken) {
            return turnstileRef.current.getFreshToken();
        }
        if (!turnstileToken) {
            throw new Error('Please complete the verification check.');
        }
        return turnstileToken;
    };

    const submitContactWorkflow = async (event) => {
        event.preventDefault();
        resetMessage();
        setSubmitting(true);
        try {
            const token = await getTurnstileToken();
            await axios.post(API_ENDPOINTS.contact, {
                name: contactForm.name,
                email: contactForm.email,
                website,
                'cf-turnstile-response': token,
                message: buildContactMessage('ACT Expo 2026 contact information submission', {
                    name: contactForm.name,
                    email: contactForm.email,
                    company: contactForm.company,
                    title: contactForm.title,
                    phone: contactForm.phone,
                    interest: contactForm.interest,
                }),
            });
            setContactForm(initialContactForm);
            setTurnstileToken('');
            setTurnstileResetKey((value) => value + 1);
            setMessage({ type: 'success', text: 'Thanks. Your contact information was submitted.' });
        } catch (error) {
            logger.error('ACT 2026 contact workflow failed:', error);
            setMessage({ type: 'error', text: error.response?.data?.error || error.message || 'Unable to submit contact information. Please try again.' });
            setTurnstileResetKey((value) => value + 1);
        } finally {
            setSubmitting(false);
        }
    };

    const submitDemoWorkflow = async (event) => {
        event.preventDefault();
        resetMessage();
        setSubmitting(true);
        try {
            const token = await getTurnstileToken();
            await axios.post(API_ENDPOINTS.contact, {
                name: demoForm.name,
                email: demoForm.email,
                website,
                'cf-turnstile-response': token,
                message: buildContactMessage('ACT Expo 2026 demo request', {
                    name: demoForm.name,
                    email: demoForm.email,
                    company: demoForm.company,
                    role: demoForm.role,
                    productInterest: demoForm.productInterest,
                    preferredTime: demoForm.preferredTime,
                    notes: demoForm.notes,
                }),
            });
            setDemoForm(initialDemoForm);
            setTurnstileToken('');
            setTurnstileResetKey((value) => value + 1);
            setMessage({ type: 'success', text: 'Demo request received. We will follow up to coordinate a time.' });
        } catch (error) {
            logger.error('ACT 2026 demo workflow failed:', error);
            setMessage({ type: 'error', text: error.response?.data?.error || error.message || 'Unable to submit demo request. Please try again.' });
            setTurnstileResetKey((value) => value + 1);
        } finally {
            setSubmitting(false);
        }
    };

    const validatePassword = () => {
        if (registrationForm.password.length < 8) {
            return 'Password must be at least 8 characters long.';
        }
        if (!/[A-Z]/.test(registrationForm.password)) {
            return 'Password must include at least one uppercase letter.';
        }
        if (!/[a-z]/.test(registrationForm.password)) {
            return 'Password must include at least one lowercase letter.';
        }
        if (!/[0-9]/.test(registrationForm.password)) {
            return 'Password must include at least one number.';
        }
        if (registrationForm.password !== registrationForm.confirmPassword) {
            return 'Passwords do not match.';
        }
        return null;
    };

    const submitRegistrationWorkflow = async (event) => {
        event.preventDefault();
        resetMessage();

        if (!registrationForm.acceptedTerms) {
            setMessage({ type: 'error', text: 'Please accept the terms and conditions to continue.' });
            return;
        }

        const passwordError = validatePassword();
        if (passwordError) {
            setMessage({ type: 'error', text: passwordError });
            return;
        }

        setSubmitting(true);
        try {
            const usernameResponse = await axios.post(API_ENDPOINTS.isUsernameAvailable, {
                username: registrationForm.username,
            });
            const isAvailable = usernameResponse.data.available?.username ?? usernameResponse.data.all_available;
            const isTaken = usernameResponse.data.taken?.username ?? false;
            if (isTaken === true || isAvailable === false) {
                setMessage({ type: 'error', text: 'Username is already taken. Please choose another username.' });
                return;
            }

            const token = await getTurnstileToken();
            await axios.post(API_ENDPOINTS.register, {
                username: registrationForm.username,
                email: registrationForm.email,
                password: registrationForm.password,
                confirm_password: registrationForm.confirmPassword,
                first_name: registrationForm.firstName,
                last_name: registrationForm.lastName,
                company_name: registrationForm.company,
                phone_number: registrationForm.phone,
                has_token: false,
                conference_source: 'act_expo_2026',
                'cf-turnstile-response': token,
            });

            setRegistrationForm(initialRegistrationForm);
            setTurnstileToken('');
            setTurnstileResetKey((value) => value + 1);
            setMessage({ type: 'success', text: 'Account created. Please check your email to verify your account.' });
        } catch (error) {
            logger.error('ACT 2026 registration workflow failed:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.error || error.message || 'Registration failed. Please try again.',
            });
            setTurnstileResetKey((value) => value + 1);
        } finally {
            setSubmitting(false);
        }
    };

    const activeOption = workflowOptions.find((option) => option.id === activeWorkflow) || workflowOptions[0];

    return (
        <div className="min-h-screen bg-slate-950 text-white">
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 pt-32 pb-20">
                <div className="absolute left-1/2 top-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <div className="inline-flex rounded-full border border-blue-300/30 bg-blue-400/10 px-4 py-2 text-sm font-semibold text-blue-100">
                            ACT Expo 2026
                        </div>
                        <h1 className="mt-6 text-4xl font-bold tracking-tight text-white md:text-6xl">
                            Connect with AmpTrans at 2026 ACT Expo!
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50/85">
                            Leave us your contact information and we will follow up after the event.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 text-slate-900">
                <div className="mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-[360px_1fr] lg:px-8">
                    <aside className="space-y-3">
                        {workflowOptions.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                    setActiveWorkflow(option.id);
                                    resetMessage();
                                }}
                                className={`w-full rounded-2xl border p-5 text-left transition ${
                                    activeWorkflow === option.id
                                        ? 'border-blue-500 bg-white shadow-lg shadow-blue-100'
                                        : 'border-slate-200 bg-white/80 hover:border-blue-200 hover:bg-white'
                                }`}
                            >
                                <div className="text-sm font-semibold text-blue-700">{option.label}</div>
                                <div className="mt-2 text-lg font-bold text-slate-900">{option.title}</div>
                                <p className="mt-2 text-sm leading-6 text-slate-600">{option.description}</p>
                            </button>
                        ))}
                    </aside>

                    <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 md:p-8">
                        <div className="mb-8">
                            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">{activeOption.label}</p>
                            <h2 className="mt-3 text-2xl font-bold text-slate-900">{activeOption.title}</h2>
                            <p className="mt-2 text-slate-600">{activeOption.description}</p>
                        </div>

                        {message.text ? (
                            <div className={`mb-6 rounded-2xl border px-4 py-3 text-sm ${
                                message.type === 'success'
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                    : 'border-red-200 bg-red-50 text-red-700'
                            }`}
                            >
                                {message.text}
                            </div>
                        ) : null}

                        {activeWorkflow === 'contact' && (
                            <form onSubmit={submitContactWorkflow} className="relative space-y-5">
                                <div className="grid gap-5 md:grid-cols-2">
                                    <Field id="contact-name" label="Full name *" value={contactForm.name} onChange={(value) => updateContactField('name', value)} required /> 
                                    <Field id="contact-email" label="Email *" type="email" value={contactForm.email} onChange={(value) => updateContactField('email', value)} required /> 
                                    <Field id="contact-company" label="Company" value={contactForm.company} onChange={(value) => updateContactField('company', value)} />
                                    <Field id="contact-title" label="Title" value={contactForm.title} onChange={(value) => updateContactField('title', value)} /> 
                                    <Field id="contact-phone" label="Phone *" type="tel" value={contactForm.phone} onChange={(value) => updateContactField('phone', value)} /> 
                                </div>
                                <p className="text-xs leading-5 text-slate-500">* Required fields</p>
                                <TextArea id="contact-interest" label="What are you interested in?" value={contactForm.interest} onChange={(value) => updateContactField('interest', value)} />
                                <HoneypotField value={website} onChange={setWebsite} />
                                <SubmitButton disabled={submitting}>Submit contact information</SubmitButton>
                                <TurnstileWidget ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} action="act-contact" resetKey={turnstileResetKey} onTokenChange={setTurnstileToken} />
                            </form>
                        )}

                        {activeWorkflow === 'demo' && (
                            <form onSubmit={submitDemoWorkflow} className="relative space-y-5">
                                <div className="grid gap-5 md:grid-cols-2">
                                    <Field id="demo-name" label="Full name *" value={demoForm.name} onChange={(value) => updateDemoField('name', value)} required />
                                    <Field id="demo-email" label="Email *" type="email" value={demoForm.email} onChange={(value) => updateDemoField('email', value)} required />
                                    <Field id="demo-company" label="Company *" value={demoForm.company} onChange={(value) => updateDemoField('company', value)} required />
                                    <Field id="demo-role" label="Role *" value={demoForm.role} onChange={(value) => updateDemoField('role', value)} />
                                </div>
                                <div>
                                    <label htmlFor="demo-product-interest" className={labelClassName}>Product interest</label>
                                    <select
                                        id="demo-product-interest"
                                        value={demoForm.productInterest}
                                        onChange={(event) => updateDemoField('productInterest', event.target.value)}
                                        className={inputClassName}
                                    >
                                        <option>Fleet planning</option>
                                        <option>Utility planning</option>
                                        <option>Charging infrastructure planning</option>
                                        <option>Regulator tools</option>
                                    </select>
                                </div>
                                <Field id="demo-preferred-time" label="Preferred meeting time *" value={demoForm.preferredTime} onChange={(value) => updateDemoField('preferredTime', value)} placeholder="Example: Wednesday afternoon at ACT Expo" required />
                                <TextArea id="demo-notes" label="What would you like to see?" value={demoForm.notes} onChange={(value) => updateDemoField('notes', value)} />
                                <p className="text-xs leading-5 text-slate-500">* Required fields</p> <br />
                                <HoneypotField value={website} onChange={setWebsite} />
                                <SubmitButton disabled={submitting}>Request a demo</SubmitButton>
                                <TurnstileWidget ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} action="act-demo" resetKey={turnstileResetKey} onTokenChange={setTurnstileToken} />
                            </form>
                        )}

                        {activeWorkflow === 'register' && (
                            <form onSubmit={submitRegistrationWorkflow} className="space-y-5">
                                <div className="grid gap-5 md:grid-cols-2">
                                    <Field id="register-first-name" label="First name *" value={registrationForm.firstName} onChange={(value) => updateRegistrationField('firstName', value)} required />
                                    <Field id="register-last-name" label="Last name *" value={registrationForm.lastName} onChange={(value) => updateRegistrationField('lastName', value)} required />
                                    <Field id="register-username" label="Username *" value={registrationForm.username} onChange={(value) => updateRegistrationField('username', value)} minLength={3} maxLength={25} required />
                                    <Field id="register-email" label="Email *" type="email" value={registrationForm.email} onChange={(value) => updateRegistrationField('email', value)} required />
                                    <Field id="register-company" label="Company" value={registrationForm.company} onChange={(value) => updateRegistrationField('company', value)} />
                                    <Field id="register-phone" label="Phone" type="tel" value={registrationForm.phone} onChange={(value) => updateRegistrationField('phone', value)} />
                                    <Field id="register-password" label="Password *" type="password" value={registrationForm.password} onChange={(value) => updateRegistrationField('password', value)} required />
                                    <Field id="register-confirm-password" label="Confirm password *" type="password" value={registrationForm.confirmPassword} onChange={(value) => updateRegistrationField('confirmPassword', value)} required />
                                </div>
                                <p className="text-xs leading-5 text-slate-500">
                                    Password must be at least 8 characters long, include one uppercase letter, one lowercase letter, and one number.
                                </p>
                                <label className="flex items-start gap-3 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={registrationForm.acceptedTerms}
                                        onChange={(event) => updateRegistrationField('acceptedTerms', event.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                                    />
                                    <span>
                                        I agree to the AmpTrans{' '}
                                        <button
                                            type="button"
                                            onClick={() => setShowTermsModal(true)}
                                            className="font-medium text-blue-700 underline underline-offset-2 transition hover:text-blue-800"
                                        >
                                            Terms and Conditions
                                        </button>
                                        . *
                                    </span>
                                </label>
                                <p className="text-xs leading-5 text-slate-500">* Required fields</p>
                                <SubmitButton disabled={submitting}>Create account</SubmitButton>
                                <TurnstileWidget ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY} action="register" resetKey={turnstileResetKey} onTokenChange={setTurnstileToken} />
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {showTermsModal ? (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">Terms and Conditions</h3>
                                <p className="mt-1 text-sm text-slate-500">Review the terms before continuing.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowTermsModal(false)}
                                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                                aria-label="Close terms and conditions"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="prose prose-sm max-w-none flex-1 overflow-y-auto px-6 py-6 text-slate-700">
                            {termsContent ? (
                                <div dangerouslySetInnerHTML={{ __html: termsContent }} />
                            ) : (
                                <p>Loading terms and conditions...</p>
                            )}
                        </div>

                        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setShowTermsModal(false)}
                                className="rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-800"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function HoneypotField({ value, onChange }) {
    return (
        <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
}

function Field({ id, label, value, onChange, type = 'text', required = false, ...props }) {
    return (
        <div>
            <label htmlFor={id} className={labelClassName}>{label}</label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                required={required}
                className={inputClassName}
                {...props}
            />
        </div>
    );
}

function TextArea({ id, label, value, onChange }) {
    return (
        <div>
            <label htmlFor={id} className={labelClassName}>{label}</label>
            <textarea
                id={id}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                rows={5}
                className={inputClassName}
            />
        </div>
    );
}

function SubmitButton({ children, disabled }) {
    return (
        <button
            type="submit"
            disabled={disabled}
            className="inline-flex w-full items-center justify-center rounded-xl bg-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
        >
            {disabled ? 'Submitting...' : children}
        </button>
    );
}

export default ACT2026Registration;
